import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { LOG_NAVIGATION_MUTATION, LOG_EVENT_MUTATION } from '../graphql/auditlog';

// Page name mapping
const PAGE_NAMES: Record<string, string> = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/sequences': 'Sequences',
  '/results': 'Risk Analysis Results',
  '/bias-results': 'Risk Analysis Results',
  '/alerts': 'Alerts',
  '/audit-log': 'Audit Log',
  '/settings': 'Settings',
};

// Get page name from path
const getPageName = (path: string): string => {
  // Check exact matches first
  if (PAGE_NAMES[path]) {
    return PAGE_NAMES[path];
  }
  
  // Check patterns
  if (path.startsWith('/sequences/')) {
    return 'Sequence Detail';
  }
  if (path.startsWith('/executions/')) {
    return 'Execution Detail';
  }
  
  return path;
};

/**
 * Hook to track navigation for audit logging
 */
export const useNavigationTracking = () => {
  const location = useLocation();
  const [logNavigation] = useMutation(LOG_NAVIGATION_MUTATION);
  const lastPathRef = useRef<string>('');
  
  useEffect(() => {
    // Only log if path actually changed (not on every render)
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      
      const pageName = getPageName(location.pathname);
      
      // Log navigation event (fire and forget)
      logNavigation({
        variables: {
          input: {
            pagePath: location.pathname,
            pageName: pageName,
          },
        },
      }).catch(() => {
        // Silently ignore errors - don't disrupt user experience
      });
    }
  }, [location.pathname, logNavigation]);
};

/**
 * Hook to log custom audit events
 */
export const useAuditLog = () => {
  const [logEventMutation] = useMutation(LOG_EVENT_MUTATION);
  
  const logEvent = async (
    category: string,
    action: string,
    title: string,
    options?: {
      description?: string;
      targetType?: string;
      targetId?: string;
      targetName?: string;
      metadata?: Record<string, any>;
    }
  ) => {
    try {
      await logEventMutation({
        variables: {
          input: {
            category,
            action,
            title,
            description: options?.description,
            targetType: options?.targetType,
            targetId: options?.targetId,
            targetName: options?.targetName,
            metadata: options?.metadata ? JSON.stringify(options.metadata) : null,
          },
        },
      });
    } catch {
      // Silently ignore errors
    }
  };
  
  return { logEvent };
};
